import { useMemo, useState } from 'react';

import preparePropertyFormSchema from '~/components/Vendors/Properties/helpers/preparePropertyFormSchema';

import { useGetPropertiesSchema } from '~/hooks/apis/accommodation/useGetProperty';

import { getPropertyRoomTypes } from '../RoomsStep/utils';
import { CREATE_NEW_PROPERTY } from '../constants';
import type { FlashOfferProperty } from '../types';
import { alwaysActionable, noResetNeeded } from '../types';

import { getMissingRequiredPropertyFields } from './utils';

export interface UsePropertyStepParams {
  defaultPropertyId: string;
  isPropertyStep: boolean;
  properties: Array<FlashOfferProperty>;
}

/**
 * Owns the Property step (2): the create-new/select-existing choice, the create-property form's
 * schema + validation, and which resolved property (created this session or pre-existing) later
 * steps (Rooms/Rate Plan/Room Rates/Capacity/Package Setup) should key off of.
 */
export function usePropertyStep({ defaultPropertyId, isPropertyStep, properties }: UsePropertyStepParams) {
  const [selectedPropertyId, setSelectedPropertyId] = useState(defaultPropertyId);
  const [createdProperty, setCreatedProperty] = useState<FlashOfferProperty | null>(null);
  const [propertyFormData, setPropertyFormData] = useState<Record<string, unknown>>({});
  const [isCreatingNewProperty, setIsCreatingNewProperty] = useState(false);

  const isCreatingProperty = selectedPropertyId === CREATE_NEW_PROPERTY;
  const isCreatePropertyStep = isPropertyStep && isCreatingProperty;

  const {
    data: createPropertySchema,
    isError: hasCreatePropertySchemaError,
    isFetching: isLoadingCreatePropertySchema,
    refetch: refetchCreatePropertySchema,
  } = useGetPropertiesSchema(isPropertyStep && isCreatingProperty);

  const propertySchema = useMemo(
    () =>
      preparePropertyFormSchema(createPropertySchema, {
        type: propertyFormData.type,
        channel_manager: propertyFormData.channel_manager,
      }),
    [createPropertySchema, propertyFormData.type, propertyFormData.channel_manager],
  );

  const isLoadingPropertyForm = isCreatingProperty && isLoadingCreatePropertySchema;
  const hasPropertyFormError = isCreatingProperty && hasCreatePropertySchemaError;
  const missingRequiredPropertyFields = getMissingRequiredPropertyFields({
    isCreatePropertyStep,
    propertyFormData,
    propertySchema,
  });
  const isPropertyStepNextDisabled =
    isCreatePropertyStep &&
    (isLoadingPropertyForm || hasPropertyFormError || !propertySchema || missingRequiredPropertyFields.length > 0);

  const selectableProperties =
    !createdProperty || properties.some((property) => property.id === createdProperty.id)
      ? properties
      : [...properties, createdProperty];
  const activeProperties = selectableProperties.filter((property) => !property.deleted_at);
  const selectedProperty = selectableProperties.find((property) => property.id === selectedPropertyId) ?? null;
  const selectedPropertyRoomTypes = useMemo(() => getPropertyRoomTypes(selectedProperty), [selectedProperty]);
  const selectedPropertyHasRooms = selectedPropertyRoomTypes.length > 0;

  function selectProperty(propertyId: string) {
    setSelectedPropertyId(propertyId);
  }

  function handlePropertyCreateStart() {
    setIsCreatingNewProperty(true);
  }

  function markPropertyCreated(property: FlashOfferProperty) {
    setCreatedProperty(property);
    setSelectedPropertyId(property.id);
  }

  function seedFromOpportunity(formData: Record<string, unknown>) {
    setPropertyFormData(formData);
  }

  function reset() {
    setSelectedPropertyId(defaultPropertyId);
    setCreatedProperty(null);
    setPropertyFormData({});
    setIsCreatingNewProperty(false);
  }

  return {
    activeProperties,
    createdProperty,
    handlePropertyCreateStart,
    hasCreatePropertySchemaError,
    hasPropertyFormError,
    isCreatePropertyStep,
    isCreatingNewProperty,
    isCreatingProperty,
    isLoadingCreatePropertySchema,
    isLoadingPropertyForm,
    isPropertyStepNextDisabled,
    isSubmissionFailureActionable: alwaysActionable,
    resetAfterFailedSubmission: noResetNeeded,
    markPropertyCreated,
    missingRequiredPropertyFields,
    propertyFormData,
    propertySchema,
    refetchCreatePropertySchema,
    reset,
    seedFromOpportunity,
    selectedProperty,
    selectedPropertyHasRooms,
    selectedPropertyId,
    selectedPropertyRoomTypes,
    selectProperty,
    setIsCreatingNewProperty,
    setPropertyFormData,
  };
}
